import React, { useCallback, useRef } from 'react';

import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { ExperimentsNavigation } from 'client/app/apps/experiments/ExperimentsNavigation';
import { ExampleWorkflow } from 'client/app/components/cards/ExampleWorkflowCard';
import { CategoriesListFetcher } from 'client/app/components/CategoriesList';
import ExampleWorkflowsDialog from 'client/app/components/ExampleWorkflows/Dialog';
import ExampleWorkflowsList from 'client/app/components/ExampleWorkflows/ExampleWorkflowsList';
import { HeaderWithDescription } from 'client/app/components/ExampleWorkflows/HeaderWithDescription';
import ContainerWithIntersectionBar from 'common/ui/components/ContainerWithIntersectionBar/ContainerWithIntersectionBar';
import useDialog from 'common/ui/hooks/useDialog';
import { useStateWithURLParams } from 'common/ui/hooks/useStateWithURLParams';

export default function ExamplesScreen() {
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useStateWithURLParams({
    paramName: 'search',
    paramType: 'string',
    defaultValue: '',
  });

  const [tagQuery, setTagQuery] = useStateWithURLParams({
    paramName: 'tag',
    paramType: 'string',
    defaultValue: '',
  });

  const [exampleWorkflowsDialog, openExampleWorkflowsDialog] =
    useDialog(ExampleWorkflowsDialog);

  const handleExampleWorkflowDetailsClick = useCallback(
    async (exampleWorkflow: ExampleWorkflow) => {
      await openExampleWorkflowsDialog({
        initialExampleWorkflow: exampleWorkflow,
        shouldShowConfirmationDialog: false,
      });
    },
    [openExampleWorkflowsDialog],
  );

  return (
    <>
      <ExperimentsNavigation title="Example workflows">
        <HeaderWithDescription
          title="Example workflows"
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          descriptionSlot={
            <div>
              <Typography variant="body1">
                Explore our collection of ready-to-use example workflows for common
                scientific use cases.
              </Typography>
              <Typography variant="body1">
                You can run these directly or customize the design to fit your needs.
              </Typography>
            </div>
          }
        />
        <CategoriesAndDataWrapper>
          <CategoriesListFetcher
            selectedTag={tagQuery}
            onSelectTag={setTagQuery}
            entityClassName="ExampleWorkflow"
          />
          <ContainerWithIntersectionBar
            scrollableRef={scrollableContainerRef}
            noHeader
            content={
              <ListWrapper>
                <ExampleWorkflowsList
                  scrollableContainerRef={scrollableContainerRef}
                  searchQuery={searchQuery}
                  tagQuery={tagQuery}
                  onExampleWorkflowDetailsClick={handleExampleWorkflowDetailsClick}
                  logCategory="examples_screen"
                />
              </ListWrapper>
            }
            dense
          />
        </CategoriesAndDataWrapper>
      </ExperimentsNavigation>
      {exampleWorkflowsDialog}
    </>
  );
}

const CategoriesAndDataWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(180px, 0.75fr) 4fr',
  columnGap: theme.spacing(8),
  overflow: 'hidden',
}));

const ListWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(6),
}));
